import { useStore } from '../store';
import { INotificationService, NotificationType, ApiError } from '@core/interfaces/INotificationService';

/**
 * Notification Service Implementation
 */
export class NotificationService implements INotificationService {
    private static instance: NotificationService;

    public static getInstance(): NotificationService {
        if (!NotificationService.instance) {
            NotificationService.instance = new NotificationService();
        }
        return NotificationService.instance;
    }

    private constructor() {
        console.log('[NotificationService] Service created');
    }

    /**
     * Format error message with properties
     */
    private formatErrorMessage(error: ApiError, fallbackMessage: string): string {
        let message = error.message ?? fallbackMessage;

        if (error.properties) {
            const properties = Array.isArray(error.properties) ? error.properties.join('<br>') : error.properties;
            message += `<br>${properties}`;
        }

        return message;
    }

    /**
     * Show success notification
     */
    public success(title: string, message?: string): void {
        const { addNotification } = useStore.getState();
        addNotification({
            type: 'success',
            title,
            message
        });
    }

    /**
     * Show error notification
     */
    public error(title: string, message?: string, duration?: number): void {
        this.clear();
        const { addNotification } = useStore.getState();
        addNotification({
            type: 'error',
            title,
            message,
            duration
        });
    }

    /**
     * Show warning notification
     */
    public warning(title: string, message?: string): void {
        const { addNotification } = useStore.getState();
        addNotification({
            type: 'warning',
            title,
            message
        });
    }

    /**
     * Show info notification
     */
    public info(title: string, message?: string): void {
        const { addNotification } = useStore.getState();
        addNotification({
            type: 'info',
            title,
            message
        });
    }

    /**
     * Show error notification from API error response
     */
    public errorResponse(error: any, t: (key: string, params?: any) => string): void {
        const apiError = error?.response?.data as ApiError;
        const errorMessage = this.formatErrorMessage(apiError, t('errors.unknownError'));

        this.error(t('error'), errorMessage);
    }

    /**
     * Show warning notification from API error response
     */
    public warningResponse(error: any, t: (key: string, params?: any) => string): void {
        const apiError = error?.response?.data as ApiError;
        const errorMessage = this.formatErrorMessage(apiError, t('errors.unknownError'));

        this.warning(t('warning'), errorMessage);
    }

    /**
     * Show notification with specific type
     */
    public notify(type: NotificationType, title: string, message?: string): void {
        const { addNotification } = useStore.getState();
        addNotification({
            type,
            title,
            message
        });
    }

    /**
     * Clear all notifications
     */
    public clear(): void {
        const { clearNotifications } = useStore.getState();
        clearNotifications();
    }
}

// Export singleton instance
export const notificationService = NotificationService.getInstance();
